import { useEffect, useReducer } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as Actions from '../../../common/services/customer/Actions';
import * as CustomerService from '../../../common/services/customer/Service';

import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import InlineSpinner from '../../../common/components/misc/InlineSpinner';
import { CustomerDataDTO } from '../../../common/types/dto/CustomerDataDTO';
import { SRPMMChartParentDTO } from '../../../common/types/dto/SRPMMChartParentDTO';
import { SRPMMChartResultDTO } from '../../../common/types/dto/SRPMMChartResultDTO';
import { RootState } from '../../base/RootReducer';
import { config } from '../../common/';
import AlertMessage from '../../common/Components/AlertMessage';
import CardsResizer from '../../common/Components/CardsResizer';
import { AppThunkDispatch } from '../../common/types';

interface PacksInfoState {
    packsMM: SRPMMChartParentDTO | undefined;
    vehcode: number;
    dealerCode: number;
    licensePlate: string;
    vin: string;
}

export interface PacksInfoProps {
    vehcode: number;
    dealerCode: number;
    licensePlate: string;
    vin: string;
}

interface ReduxStateProps {
    isCommunicating: boolean;
    customerInfo: CustomerDataDTO | undefined;
    vehicleSRPMMisCommunicating: boolean;
    vehicleSRPMMdata: SRPMMChartResultDTO;
}

interface ReduxDispatchProps {
    customerActions: typeof Actions;
    customerServiceActions: CustomerService.ServiceActions;
}

type PacksInfoProperties =
    ReduxStateProps
    & ReduxDispatchProps
    & PacksInfoProps;

const PacksInfo: React.FC<PacksInfoProperties> = (Props) => {
    const initialState: PacksInfoState = {
        packsMM: undefined,
        vehcode: -1,
        dealerCode: -1,
        licensePlate: '',
        vin: '',
    };
    const [state, setState]: [PacksInfoState, React.Dispatch<any>] = useReducer(
        (oldState, newState) => ({ ...oldState, ...newState }), initialState,
    );

    const { t } = useTranslation();

    useEffect(() => {
        if (Props.dealerCode <= 0) {
            setState({
                packsMM: undefined,
            });
            return;
        }

        if (!state.packsMM && (!Props.vehcode || Props.vehcode === -1)) {
            setState({
                packsMM: [],
            });
        } else {
            if (Props.dealerCode <= 0) {
                return;
            }
            setState({
                vehcode: Props.vehcode,
                dealerCode: Props.dealerCode,
                licensePlate: Props.licensePlate,
                vin: Props.vin,
                packsMM: undefined,
            });
            if (config.UseSRPMM) {
                if (Props.licensePlate.length === 8 || Props.vin.length === 17) {
                    Props.customerServiceActions.vehicleSRPMMByPlateOrVin(Props.dealerCode, Props.licensePlate, Props.vin, 'Service');
                } else if (Props.vehcode > 0) {
                    Props.customerServiceActions.vehicleSRPMM(Props.vehcode, Props.dealerCode, 'Service');
                }
            }
        }
    }, [Props.vehcode, Props.dealerCode]);

    useEffect(() => {
        if (Props.vehicleSRPMMdata) {
            if (Props.vehicleSRPMMdata.vehcode !== Props.vehcode) {
                return;
            }
            setState({ packsMM: Props.vehicleSRPMMdata.srpmmChart });
        }
    }, [Props.vehicleSRPMMdata]);

    function buildPacks() {
        let table: JSX.Element;
        if (state.packsMM) {
            if (!state.packsMM.isPlateAgeValid) {
                table = (<AlertMessage type={'info'} msg={t('contractsinvoices:packs.invalidAge')} />);
            } else if (state.packsMM.chart && state.packsMM.chart.length > 0) {
                const rows: JSX.Element[] = state.packsMM.chart.map(
                    (i, idx) => {
                        return (
                            <div className={classNames(['cell', 'medium-3', 'pacotesOferta'])} data-equalizer-watch="bloco-pacotes" key={idx}>
                                <div className={'card'}>
                                    <div className={classNames(['card-section', 'info'])} style={{ height: 'auto' }}>
                                        <p>
                                            {i.description}
                                        </p>
                                    </div>
                                    <div className={classNames(['card-section', 'preco'])}>
                                        <p>
                                            {i.total_price_gros + '€'}
                                        </p>
                                    </div>
                                    <div className={'card-image'}>
                                        <img src={config.VCPackagesImagesPath + 'svg/' + i.group_code + '_rg.svg'} width="150" />
                                    </div>
                                </div>
                            </div>
                        );
                    });

                table = (
                    <React.Fragment>
                        <div className={classNames(['grid-container', 'fluid'])}>
                            <div className={classNames(['grid-x', 'grid-margin-x'])} data-equalizer="bloco-pacotes" data-resize="bloco-pacotes" data-mutate="bloco-pacotes" data-events="mutate">
                                {rows}
                            </div>
                        </div>
                    </React.Fragment>
                );
            } else {
                table = (<AlertMessage type={'secondary'} msg={t(`contractsinvoices:packs.notFound`)} />);
            }

            return table;
        }
        return null;
    }

    // <div className={css.exportContainer}><FontAwesomeIcon icon={farFilePdf} /><p>{t('contractsinvoices:packs.export')}</p></div>  removed from <div className={css.packsHeader}>
    return (
        <React.Fragment>
            <div className={classNames(['columns', 'medium-12', 'infoPacotesViaturas'])}>
                <h3 className={'pacote-info-viatura'}>
                    {t(`contractsinvoices:packs.header2`)}
                </h3>
                <p>{t('contractsinvoices:packs.note1')}</p>
                <p>{t('contractsinvoices:packs.note2')}</p>
                <p>{t('contractsinvoices:packs.note3')}</p>
                <p>{t('contractsinvoices:packs.note4')}</p>
                <p>{t('contractsinvoices:packs.note5')}</p>
            </div>
            <div className={classNames(['columns', 'medium-12', 'margin-top-2'])}>
                {(state.packsMM) && Props.vehcode && Props.vehcode !== -1 && buildPacks()}
                {Props.vehicleSRPMMisCommunicating && <InlineSpinner size={'100px'} />}
            </div>
            <div className={classNames(['columns', 'medium-12', 'margin-top-1'])}>
                <label>{t(`contractsinvoices:packs.footer1`)}</label>
                <label>{t(`contractsinvoices:packs.footer2`)}</label>
                {/*<label>{t(`contractsinvoices:packs.footer3`)}</label>*/}
            </div>
            <p className={'margin-top-1'}>
                {t(`contractsinvoices:moreInfoFooter`)} &nbsp;
                <a href={config.AdditionalInfoPacks} target="_blank">{t('contractsinvoices:packs.additionalInfo')}</a>
            </p>
            <CardsResizer trigger1={state.packsMM} trigger2={Props.vehicleSRPMMdata} />
        </React.Fragment>
    );
};

const mapStateToProps = (store: RootState) => ({
    isCommunicating: store.customerState.updateCustomer.isCommunicating,
    customerInfo: store.customerState.customerData.data.customerData,
    vehicleSRPMMisCommunicating: store.customerState.vehicleSRPMM.isCommunicating,
    vehicleSRPMMdata: store.customerState.vehicleSRPMM.data,
});

const mapDispatchToProps = (dispatch: AppThunkDispatch) => ({
    customerActions: bindActionCreators(Actions, dispatch),
    customerServiceActions: CustomerService.bindServiceActionCreators(config, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(PacksInfo);
